"use client";
import React, { useEffect, useState, useRef } from "react";
import premium from "./premium.module.css";
import RazorpaySubscribeButton, { RazorpaySubscribeButtonHandle } from "./paybtns/RazorpaySubscribeButton";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { PremiumCrossIcon, PremiumTickIcon } from "../component/videoplayer/PlayerIcons";
import { fetchWithLoader } from "../utils/api";
import PaypalSubscribeButton from "./paybtns/PaypalSubscribeButton";
import { useAuthStore } from "../store/authstore";
import PremiumModal from "./modal/PremiumModal";

type Plan = {
  id: number;
  discount: string;
  plan_id: string;
  plan_type: string;
  price: string;
  status: number;
};
type PremiumData = {
  premium: string;
  product_id: string;
  purchase_token: string;
  expiry: string;
  autoRenewing: string;
  source: string;
  originalTX: string;
  plan_type: string;
};

export default function Premium() {
  // const [cardShow, setCardShow] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("Elite"); // "Plus" or "Elite"
  const [selectedDuration, setSelectedDuration] = useState("Monthly"); // "Weekly" or "Monthly"
  const [planId, setPlanId] = useState("");
  const [allPlans, setAllPlans] = useState<Plan[]>([]);
  const [planData, setPlanData] = useState<Plan | null>(null);
  const [currentCountry, setCurrentCountry] = useState<string | null>(null);
  const [CCCardShow, setCCCardShow] = useState(false);
  const [paypalCardShow, setPaypalCardShow] = useState(false);
  // const [razorpayCardShow, setRazorpayCardShow] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activePaymentGateway, setActivePaymentGateway] = useState<'paypal' | 'razorpay' | null>(null);
  const razorpayRef = useRef<RazorpaySubscribeButtonHandle>(null);

  const user = useAuthStore((state) => state.user);
  const router = useRouter();

  const premiumData = (user?.premium_data?.premium === "1"
    ? user.premium_data : undefined) as PremiumData | undefined;
  // const premiumData: PremiumData | undefined = { // Mocked premium data for testing
  //   premium: "1",
  //   product_id: "1_weekly_df_plus_subs",
  //   purchase_token: "khblfolljknfmma_gPMA8nWStdA_QEQAeaG4Ysdfdf",
  //   expiry: "2060-10-20 00:54:24",
  //   autoRenewing: "false",
  //   source: "Android",
  //   originalTX: "",
  //   plan_type: "Monthly Plus", // Test: Weekly Plus | Weekly Elite | Monthly Plus | Monthly Elite
  // };


  useEffect(() => { //plan selection weekly, monthlyd
    if (premiumData && premiumData.premium == "1") {

      // Use business logic: always default to Monthly tab for any premium user
      setSelectedDuration("Monthly");
      setSelectedPlan("Elite"); // Always default to Elite plan
    } else {
      setSelectedDuration("Monthly");
      setSelectedPlan("Elite");
    }
  }, [premiumData, premiumData?.plan_type]);

  // --- 2. Fetch Plan Data from API ---
  useEffect(() => {
    const getCountry = async () => {
      try {
        const res = await fetchWithLoader('check_country.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        const data = await res.json();
        // console.log("User country data:", data);
        if (data?.data?.country) {
          setCurrentCountry(data.data.country);
          if(data.data?.country==="IN"){
            fetchPlanData('https://dramasstory.com/web/getrazorpayplans.php',true);
          }else{
            fetchPlanData('https://payment.dramaflicksapp.com/api/v1/getallplans')
          }
        }
      } catch (error) {
        console.error("Failed to fetch country data:", error);
      }
    };
    getCountry();
    const fetchPlanData = async (url: string, isIndia: boolean = false) => {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Access-Control-Allow-Origin": "*" },
        });
        const data = await response.json();
        // console.log(data,'isIndia',isIndia)
        if(data && data.data || data && data.data.data){
          setAllPlans(isIndia ? data.data.data.slice(0,5) : data.data);
        }
      } catch (error) {
        console.error("Failed to fetch plan data:", error);
      }
    };
  }, []);

  // --- 3. Derived State for Cleaner Logic ---
  const currentUserPlanInfo = (() => {
    if (!premiumData) return null;
    const planTypeStr = premiumData.plan_type.toLowerCase();
    return {
      duration: planTypeStr.includes("weekly") ? "Weekly" : "Monthly",
      plan: planTypeStr.includes("plus") ? "Plus" : "Elite",
    };
  })();


  // Helper: get disabling logic for tabs and cards
  const getDisableLogic = () => {
    if (!premiumData || (premiumData && premiumData.premium !== "1")) {
      return {
        disableWeeklyTab: false,
        disableMonthlyTab: false,
        // Feature cards (current plan indicators)
        disableWeeklyPlusFeature: false,
        disableWeeklyEliteFeature: false,
        disableMonthlyPlusFeature: false,
        disableMonthlyEliteFeature: false,
        // Plan cards (upgrade/subscription options)
        disableWeeklyPlus: false,
        disableWeeklyElite: false,
        disableMonthlyPlus: false,
        disableMonthlyElite: false,
        defaultTab: "Monthly",
        defaultPlan: "Elite",
      };
    }
    const planType = premiumData.plan_type;
    if (planType === "Weekly Plus") {
      return {
        disableWeeklyTab: false,
        disableMonthlyTab: false,
        // Feature cards
        disableWeeklyPlusFeature: true,    // Current plan disabled in feature
        disableWeeklyEliteFeature: false,  // Elite feature enabled
        disableMonthlyPlusFeature: false,
        disableMonthlyEliteFeature: false,
        // Plan cards
        disableWeeklyPlus: true,           // Current plan disabled
        disableWeeklyElite: false,         // Can upgrade to Elite
        disableMonthlyPlus: false,
        disableMonthlyElite: false,
        defaultTab: "Monthly",
        defaultPlan: "Elite",
      };
    }
    if (planType === "Weekly Elite") {
      return {
        disableWeeklyTab: false,
        disableMonthlyTab: false,
        // Feature cards
        disableWeeklyPlusFeature: true,    // Plus feature disabled (can't downgrade)
        disableWeeklyEliteFeature: true,   // Current plan disabled in feature
        disableMonthlyPlusFeature: false,
        disableMonthlyEliteFeature: false,
        // Plan cards
        disableWeeklyPlus: true,           // Can't downgrade
        disableWeeklyElite: true,          // Current plan disabled
        disableMonthlyPlus: false,
        disableMonthlyElite: false,
        defaultTab: "Monthly",
        defaultPlan: "Elite",
      };
    }
    if (planType === "Monthly Plus") {
      return {
        disableWeeklyTab: true,            // Weekly tab NOT switchable
        disableMonthlyTab: false,
        // Feature cards
        disableWeeklyPlusFeature: true,
        disableWeeklyEliteFeature: true,
        disableMonthlyPlusFeature: true,   // Current plan disabled in feature
        disableMonthlyEliteFeature: false, // Elite feature enabled
        // Plan cards
        disableWeeklyPlus: true,
        disableWeeklyElite: true,
        disableMonthlyPlus: true,          // Current plan disabled
        disableMonthlyElite: false,        // Can upgrade
        defaultTab: "Monthly",
        defaultPlan: "Elite",
      };
    }
    if (planType === "Monthly Elite") {
      return {
        disableWeeklyTab: true,            // Weekly tab NOT switchable
        disableMonthlyTab: false,
        // Feature cards
        disableWeeklyPlusFeature: true,
        disableWeeklyEliteFeature: true,
        disableMonthlyPlusFeature: true,   // Plus feature disabled (can't downgrade)
        disableMonthlyEliteFeature: true,  // Current plan disabled in feature
        // Plan cards
        disableWeeklyPlus: true,
        disableWeeklyElite: true,
        disableMonthlyPlus: true,          // Can't downgrade
        disableMonthlyElite: true,         // Current plan disabled
        defaultTab: "Monthly",
        defaultPlan: "Elite",
      };
    }
    // fallback
    return {
      disableWeeklyTab: false,
      disableMonthlyTab: false,
      disableWeeklyPlusFeature: false,
      disableWeeklyEliteFeature: false,
      disableMonthlyPlusFeature: false,
      disableMonthlyEliteFeature: false,
      disableWeeklyPlus: false,
      disableWeeklyElite: false,
      disableMonthlyPlus: false,
      disableMonthlyElite: false,
      defaultTab: "Monthly",
      defaultPlan: "Elite",
    };
  };

  // Use disabling logic for UI
  const disableLogic = getDisableLogic();
  // On tab change, update selection according to logic
  const handleDurationChange = (duration: string) => {
    if (disableLogic.disableWeeklyTab && duration === "Weekly") return;
    if (disableLogic.disableMonthlyTab && duration === "Monthly") return;
    setSelectedDuration(duration);

    // Always select Elite if not logged in or not premium
    if (!user || !premiumData || (premiumData && premiumData.premium !== "1")) {
      setSelectedPlan("Elite");
      return;
    }

    // When switching tabs, select the appropriate plan based on availability
    if (duration === "Weekly") {
      if (disableLogic.disableWeeklyPlus && disableLogic.disableWeeklyElite) {
        return;
      }
      setSelectedPlan(disableLogic.disableWeeklyElite ? "Plus" : "Elite");
    } else {
      if (disableLogic.disableMonthlyPlus && disableLogic.disableMonthlyElite) {
        return;
      }
      setSelectedPlan(disableLogic.disableMonthlyElite ? "Plus" : "Elite");
    }
  };

  // On plan change, update selection according to logic
  const handlePlanChange = (planType: string) => {
    // Check if the plan is disabled for current duration
    if (selectedDuration === "Weekly") {
      if ((planType === "Plus" && disableLogic.disableWeeklyPlus) ||
        (planType === "Elite" && disableLogic.disableWeeklyElite)) return;
    } else {
      if ((planType === "Plus" && disableLogic.disableMonthlyPlus) ||
        (planType === "Elite" && disableLogic.disableMonthlyElite)) return;
    }
    setSelectedPlan(planType);
  };

  // --- 4. Robust handleSubmit ---
  const handleSubmit = (e: React.FormEvent, paymentGateway: 'paypal' | 'razorpay' = 'paypal') => {
    // Plan ID                       Plan Name               Index
    // P-7GY98777EN222962RNCK5DSQ    DramaFlicks Elite Monthly 0
    // P-62185365MG8474922NCK5A7Q    DramaFlicks Elite Weekly  1
    // P-5KD84229V3052522HNCK42CY    DramaFlicks Plus Monthly  2
    // P-08V614242A618723MNCK4Y7Y    DramaFlicks Plus Weekly   3
    e.preventDefault();
    if (!user && currentCountry !== "IN") {
      router.push("/login");
      return;
    }

    if (currentCountry === "IN" && paymentGateway === 'paypal') {
      setCCCardShow(true);
      // setRazorpayCardShow(true);

      return;
    }
    const targetPlanName = `DramaFlicks ${selectedPlan} ${selectedDuration}`;
    const selectedPlanData = allPlans.find(p => p.plan_type === targetPlanName);
    console.log(paymentGateway)
    if (selectedPlanData) {
      // console.log(selectedPlanData)
      setPlanData(selectedPlanData);
      setPlanId(selectedPlanData.plan_id);
      setActivePaymentGateway(paymentGateway);
      if (paymentGateway === 'paypal') {
        setPaypalCardShow(true);
      } else if (paymentGateway === 'razorpay') {
        // Directly trigger Razorpay payment without showing card dialog
        setTimeout(() => {
          razorpayRef.current?.openPayment();
        }, 100);
      }
    } else {
      console.error("Selected plan not found in API data:", targetPlanName);
    }
  };

  // --- 5. Helper to find plan details ---
  const getPlanDetails = (duration: string, plan: string): Plan | null => {
    if (allPlans.length === 0) return null;
    const planName = `DramaFlicks ${plan} ${duration}`;
    return allPlans.find(p => p.plan_type === planName) ?? null;
  };

  const plusPlanDetails = getPlanDetails(selectedDuration, "Plus");
  const elitePlanDetails = getPlanDetails(selectedDuration, "Elite");

  // --- 6. Reusable Price Display Component ---
  // const PriceDisplay = ({ planDetails }: { planDetails: Plan | null }) => {
  //   if (!planDetails) {
  //     return <div className={premium.cardStandardPrice}>Loading...</div>;
  //   }

  //   const isMonthly = selectedDuration === 'Monthly';
  //   const originalPrice = parseFloat(planDetails.price);
  //   const discountPrice = parseFloat(planDetails.discount);

  //   const discountPercent = isMonthly && originalPrice > discountPrice
  //     ? Math.round(((originalPrice - discountPrice) / originalPrice) * 100)
  //     : 0;
  //     // console.log(originalPrice)
  //   return (
  //     <>
  //       <div className={premium.cardStandardPrice}>
  //         {isMonthly && discountPercent > 0 && ( //discountPercent > 0
  //           <div className={premium.crossPrice}>
  //             <p className={premium.priceLinecross}>{currentCountry === "IN" ? "₹" : "$"} {originalPrice.toFixed(2)}</p>
  //           </div>
  //         )}
  //         <div className={premium.priceOriginal}>
  //           <span className={premium.Price}>
  //             {currentCountry === "IN" ? "₹" : "$"}{isMonthly && discountPercent==0 ? discountPrice.toFixed(2) : originalPrice.toFixed(2)}
  //           </span>
  //           <p className={premium.days}>/{isMonthly ? "mo" : "wk"}</p>
  //           {/* <p className={premium.months}>/mo</p> */}
  //         </div>
  //       </div>
  //       {isMonthly && discountPercent > 0 && discountPercent < 100 && (
  //         <span className={premium.offer}>
  //           <h5 className={premium.offprice}>Save {discountPercent}% Off</h5>
  //         </span>
  //       )}
  //     </>
  //   );
  // };

  const PriceDisplay = ({ planDetails, planType }: { planDetails: Plan | null, planType: 'Plus' | 'Elite' }) => {
    if (!planDetails) {
      return <div className={premium.cardStandardPrice}>Loading...</div>;
    }

    const isMonthly = selectedDuration === 'Monthly';
    const originalPrice = parseFloat(planDetails.price);
    const discountPrice = parseFloat(planDetails.discount);

    // Find weekly plan to calculate monthly cross price
    const weeklyPlanDetails = getPlanDetails('Weekly', planType);
    const weeklyPrice = weeklyPlanDetails ? parseFloat(weeklyPlanDetails.price) : 0;
    const monthlyCrossPrice = weeklyPrice * 4;

    const showCrossPrice = isMonthly && monthlyCrossPrice > discountPrice;

    const discountPercent = showCrossPrice
      ? Math.round(((monthlyCrossPrice - discountPrice) / monthlyCrossPrice) * 100)
      : 0;

    return (
      <>
        <div className={premium.cardStandardPrice}>
          {showCrossPrice && (
            <div className={premium.crossPrice}>
              <p className={premium.priceLinecross}>{currentCountry === "IN" ? "₹" : "$"} {monthlyCrossPrice.toFixed(2)}</p>
            </div>
          )}
          <div className={premium.priceOriginal}>
            <span className={premium.Price}>
              {currentCountry === "IN" ? "₹" : "$"}{originalPrice.toFixed(2)}
            </span>
            <p className={premium.days}>/{isMonthly ? "mo" : "wk"}</p>
          </div>
        </div>
        {showCrossPrice && discountPercent > 0 && discountPercent < 100 && (
          <span className={premium.offer}>
            <h5 className={premium.offprice}>Save {discountPercent}% Off</h5>
          </span>
        )}
      </>
    );
  };


  return (
    <div>
      {/* Razorpay component - no UI, just ref-based control */}
      {/* <RazorpaySubscribeButton
        ref={razorpayRef}
        planId={planId}
        amount={planData?.price || "0.00"}
        discount={planData?.discount || "0.00"}
        planType={planData?.plan_type || ""}
      /> */}

      {paypalCardShow && <div className={premium.paymentcard}>
        <div className={premium.innercard} >
          <PaypalSubscribeButton
            planId={planId}
            amount={planData!.price || "0.00"}
            discount={planData!.discount || "0.00"}
            planType={planData!.plan_type || ""}
          />
          <div className={premium.clear} onClick={() => setPaypalCardShow(false)}>+</div>
        </div>
      </div>}

      {/* {razorpayCardShow && <div className={premium.paymentcard}>
        <div className={premium.innercard} >
          <RazorpaySubscribeButton
            ref={razorpayRef}
            planId={planId}
            amount={planData!.price || "0.00"}
            discount={planData!.discount || "0.00"}
            planType={planData!.plan_type || ""}
          />
          <div className={premium.clear} onClick={() => setRazorpayCardShow(false)}>+</div>
        </div>
      </div>} */}

      {CCCardShow && <div className={premium.paymentcard}>
        <div className={premium.innercard}>
          <PremiumModal onclick={() => setCCCardShow(false)} />
        </div>
      </div>}

      <div className={premium.premiumSec}>
        <div className="container">
          <h2>Subscribe and enjoy unlimited dramas.</h2>
          <p>Choose the plan that&#39;s right for you.</p>

          <div className={premium.PreTitle}>
            <div className={premium.Wrapper}>
              <label htmlFor="cardtitle_1" className={premium.radioCard}>
                <input
                  type="radio"
                  name="cardtitle"
                  id="cardtitle_1"
                  checked={selectedDuration === "Weekly"}
                  onChange={() => handleDurationChange("Weekly")}
                  disabled={disableLogic.disableWeeklyTab}
                />
                <span>Weekly</span>
              </label>
              <label htmlFor="cardtitle_2" className={premium.radioCard}>
                <input
                  type="radio"
                  name="cardtitle"
                  id="cardtitle_2"
                  checked={selectedDuration === "Monthly"}
                  onChange={() => handleDurationChange("Monthly")}
                  disabled={disableLogic.disableMonthlyTab}
                />
                <span>Monthly</span>
              </label>
            </div>
          </div>


          <div className={`${premium.gridWrapper} ${premium.gridColAuto}`}>
            <div className={`${premium.cardContentWrapper} ${premium.cardWidth}`}>
              <span className={premium.checkIcon}></span>
              <div className={premium.cardContent}>
                <h4 className={premium.features}>Feature </h4>
                <ul className={premium.AllContent}>
                  <li>Unlock all episodes</li>
                  <li>Watch on 3 devices</li>
                  <li>All Upcoming Dramas</li>
                  {/* <li>Custom Playback speed</li> */}
                  <li>Ad-free </li>
                  {/* <li>Watch Offline</li>
                  <li>Max Video Quality </li> */}
                </ul>
              </div>
            </div>

            <div className={premium.cardsetWidth}>
              <label htmlFor="radio-card-1" className={premium.radioCard}>
                <input
                  type="radio"
                  name="radio-card"
                  id="radio-card-1"
                  checked={selectedPlan === "Plus"}
                  onChange={() => handlePlanChange("Plus")}
                  disabled={selectedDuration === "Weekly" ? disableLogic.disableWeeklyPlusFeature : disableLogic.disableMonthlyPlusFeature}
                />
                <div
                  className={`${premium.cardContentWrapper} ${
                    // Only show ActiveTab if plan is selected AND not the user's current plan (not disabled)
                    selectedPlan === "Plus" && !(
                      (selectedDuration === "Weekly" && disableLogic.disableWeeklyPlusFeature) ||
                      (selectedDuration === "Monthly" && disableLogic.disableMonthlyPlusFeature)
                    ) ? premium.ActiveTab : ""
                    } ${
                    // Show disabledTab if this is the user's current plan
                    (selectedDuration === "Weekly" && disableLogic.disableWeeklyPlusFeature) ||
                      (selectedDuration === "Monthly" && disableLogic.disableMonthlyPlusFeature) ? premium.disabledTab : ""
                    }`}
                >
                  <span className={premium.checkIcon}></span>
                  <div className={premium.cardContent}>
                    {/* <h4 className={premium.Plantitle}>Standard</h4> */}
                    <h4 className={premium.PlantitleNew}>Plus</h4>
                    <ul>
                      <li><PremiumTickIcon /></li>
                      <li><PremiumTickIcon /></li>
                      <li><PremiumTickIcon /></li>
                      {/* <li><PremiumTickIcon /></li> */}
                      <li className={premium.Colradd}><PremiumCrossIcon /></li>
                      {/* <li className={premium.Colradd}><PremiumCrossIcon /></li>
                      <li className={premium.qualityNum}>720p</li> */}
                    </ul>
                  </div>
                </div>
              </label>
            </div>

            <div className={premium.cardsetWidth}>
              <label htmlFor="radio-card-2" className={premium.radioCard}>
                <input
                  type="radio"
                  name="radio-card"
                  id="radio-card-2"
                  checked={selectedPlan === "Elite"}
                  onChange={() => handlePlanChange("Elite")}
                  disabled={selectedDuration === "Weekly" ? disableLogic.disableWeeklyEliteFeature : disableLogic.disableMonthlyEliteFeature}
                />
                <div className={`${premium.cardContentWrapper} ${
                  // Only show ActiveTab if plan is selected AND not the user's current plan (not disabled)
                  selectedPlan === "Elite" && !(
                    (selectedDuration === "Weekly" && disableLogic.disableWeeklyEliteFeature) ||
                    (selectedDuration === "Monthly" && disableLogic.disableMonthlyEliteFeature)
                  ) ? premium.ActiveTab : ""
                  } ${
                  // Show disabledTab if this is the user's current plan
                  (selectedDuration === "Weekly" && disableLogic.disableWeeklyEliteFeature) ||
                    (selectedDuration === "Monthly" && disableLogic.disableMonthlyEliteFeature) ? premium.disabledTab : ""
                  }`}
                >
                  <span className={premium.checkIcon}></span>
                  <div className={premium.cardContent}>
                    {/* <h4 className={premium.Plantitle}>Premium</h4> */}
                    <h4 className={premium.PlantitleNew}>Elite</h4>
                    <ul>
                      <li><PremiumTickIcon /></li>
                      <li><PremiumTickIcon /></li>
                      <li><PremiumTickIcon /></li>
                      {/* <li><PremiumTickIcon /></li> */}
                      <li><PremiumTickIcon /></li>
                      {/* <li><PremiumTickIcon /></li>
                      <li className={premium.qualityNum}>1080p</li> */}
                    </ul>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className={`${premium.gridWrap} ${premium.SecondTab} `}>
            {/* --- PLUS PLAN CARD --- */}
            <div className={`${premium.cardWidthTab} ${
              // Only show Active if plan is selected AND not disabled (not current plan)
              selectedPlan === "Plus" && !(
                (selectedDuration === "Weekly" && disableLogic.disableWeeklyPlus) ||
                (selectedDuration === "Monthly" && disableLogic.disableMonthlyPlus)
              ) ? premium.Active : ""
              } ${
              // Show disabled if this is the user's current plan
              (selectedDuration === "Weekly" && disableLogic.disableWeeklyPlus) ||
                (selectedDuration === "Monthly" && disableLogic.disableMonthlyPlus) ? premium.disabled : ""
              } `} >
              <label htmlFor="radio-card-3" className={premium.radioCard}>
                {(() => {
                  const isCurrent = currentUserPlanInfo?.duration === selectedDuration && currentUserPlanInfo?.plan === "Plus";
                  // Disable if it's the current plan OR if the user has an Elite plan on this tab
                  const isDisabled = isCurrent || (currentUserPlanInfo?.duration === selectedDuration && currentUserPlanInfo?.plan === "Elite");
                  const price = plusPlanDetails ? (selectedDuration === 'Monthly' ? Number(plusPlanDetails.discount) : Number(plusPlanDetails.price)) : 0;
                  const daysInPeriod = selectedDuration === 'Weekly' ? 7 : 30;
                  const perDayCost = (Number(plusPlanDetails?.price) / daysInPeriod).toFixed(2);
                  return (
                    <>
                      <input
                        type="radio"
                        name="radio-card3"
                        id="radio-card-3"
                        checked={isDisabled}
                        onChange={() => handlePlanChange("Plus")}
                        disabled={isDisabled}
                      />
                      <div className={`${premium.cardContentWrapper} ${isCurrent ? premium.currentPlan : ''}`}>
                        <div className={premium.cardStandard}>
                          <label className={premium.customCheckbox}>
                            <input
                              type="checkbox"
                              checked={selectedPlan === "Plus"}
                              onChange={() => handlePlanChange("Plus")}
                              disabled={isDisabled}
                            />
                            <span className={premium.checkmark}></span>
                          </label>
                          <div className={premium.carddetailsplan}>
                            <h3 className={isCurrent ? premium.currentPlanTitle : ''}>
                              {isCurrent ? 'Current Plan' : 'Plus'}
                            </h3>
                            <span className={premium.remainTime}>
                              {isCurrent ? 'Plus' : `(1 ${selectedDuration === 'Weekly' ? 'week' : 'month'} at ${currentCountry === "IN" ? "₹" : "$"}${perDayCost}/day)`}
                            </span>
                          </div>
                        </div>
                        <PriceDisplay planDetails={plusPlanDetails} planType="Plus" />
                      </div>
                    </>
                  );
                })()}
              </label>
            </div>

            {/* --- ELITE PLAN CARD --- */}
            <div className={`${premium.cardWidthTab} ${
              // Only show Active if plan is selected AND not disabled (not current plan)  
              selectedPlan === "Elite" && !(
                (selectedDuration === "Weekly" && disableLogic.disableWeeklyElite) ||
                (selectedDuration === "Monthly" && disableLogic.disableMonthlyElite)
              ) ? premium.Active : ""
              } ${
              // Show disabled if this is the user's current plan
              (selectedDuration === "Weekly" && disableLogic.disableWeeklyElite) ||
                (selectedDuration === "Monthly" && disableLogic.disableMonthlyElite) ? premium.disabled : ""
              } `}>
              <label htmlFor="radio-card-4" className={premium.radioCard}>
                {(() => {
                  const isCurrent = currentUserPlanInfo?.duration === selectedDuration && currentUserPlanInfo?.plan === "Elite";
                  const price = elitePlanDetails ? (selectedDuration === 'Monthly' ? Number(elitePlanDetails.discount) : Number(elitePlanDetails.price)) : 0;
                  const daysInPeriod = selectedDuration === 'Weekly' ? 7 : 30;
                  const perDayCost = (Number(elitePlanDetails?.price) / daysInPeriod).toFixed(2);
                  return (
                    <>
                      <input
                        type="radio"
                        name="radio-card3"
                        id="radio-card-4"
                        checked={selectedPlan === "Elite"}
                        onChange={() => handlePlanChange("Elite")}
                        disabled={isCurrent}
                      />
                      <div className={`${premium.cardContentWrapper}`}>
                        <div className={premium.cardStandard}>
                          <label className={premium.customCheckbox}>
                            <input
                              type="checkbox"
                              checked={selectedPlan === "Elite"}
                              onChange={() => handlePlanChange("Elite")}
                              disabled={isCurrent}
                            />
                            <span className={premium.checkmark}></span>
                          </label>
                          <div className={premium.carddetailsplan}>
                            <h3 className={isCurrent ? premium.currentPlanTitle : ''}>
                              {isCurrent ? 'Current Plan' : 'Elite'}
                            </h3>
                            <span className={premium.remainTime}>
                              {isCurrent ? 'Elite' : `(1 ${selectedDuration === 'Weekly' ? 'week' : 'month'} at ${currentCountry === "IN" ? "₹" : "$"}${perDayCost}/day)`}
                            </span>
                          </div>
                        </div>
                        <PriceDisplay planDetails={elitePlanDetails} planType="Elite" />
                      </div>
                    </>
                  );
                })()}
              </label>
            </div>
          </div>

          {/* Show continue button only if upgrades are available on current selected tab */}
          {(() => {
            // Check if any plan is available (not disabled) on the current selected tab
            const hasUpgradeOptions = selectedDuration === "Weekly"
              ? (!disableLogic.disableWeeklyPlus || !disableLogic.disableWeeklyElite)
              : (!disableLogic.disableMonthlyPlus || !disableLogic.disableMonthlyElite);

            // Also check if user is not premium (can subscribe to anything)
            const isNotPremium = !premiumData || premiumData.premium !== "1";

            return (hasUpgradeOptions || isNotPremium) && (
              <div className={premium.premiumBtn}>
                {/* {currentCountry !== "IN" && */}
                  <button className={`${premium.paymentByApp} commonBtn`} onClick={(e) => handleSubmit(e, 'paypal')}>
                    {/* {user && user?.premium_data?.premium == "1" ? 'Upgrade' : 'Continue'} With {selectedPlan} */}
                    <span className={premium.commonBtnIcon}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="36" viewBox="0 0 26 36" fill="none">
                        <path d="M22.3758 16.5962L11.9567 10.6884L18.2058 4.76401C18.6042 5.01674 19.0765 5.16339 19.5829 5.16339C21.0061 5.16339 22.159 4.00951 22.159 2.58721C22.159 1.16491 21.0052 0.0101057 19.5829 0.0101057C18.1606 0.0101057 17.0067 1.16399 17.0067 2.58629C17.0067 3.01796 17.1137 3.42472 17.3009 3.7826L10.7373 9.99662L8.41018 8.67763C8.1593 8.53282 7.9038 8.41014 7.64646 8.30868L4.0861 3.21811C4.27242 2.96077 4.40156 2.65731 4.44952 2.32157C4.61093 1.21103 3.84168 0.181667 2.73206 0.0211741C1.62337 -0.139318 0.594007 0.629939 0.433515 1.73863C0.273022 2.84824 1.04228 3.87761 2.15189 4.0381C2.44336 4.08053 2.72837 4.05655 2.99402 3.98184L5.74822 7.91944C2.76804 7.84842 0 10.2115 0 13.5339V21.7821L7.67395 15.7354L21.2171 27.089L21.3401 27.0157L22.4136 26.3955C26.1944 24.2131 26.1732 18.749 22.3758 16.5962Z" fill="#EA2829" />
                        <path d="M0 21.7044V21.6749V29.5806V29.5335L7.48135 22.934L15.8795 30.1672L15.862 30.1773L21.2275 27.08L7.71471 15.0449L0 21.7044Z" fill="url(#paint0_linear_6572_1055)" />
                        <path d="M0 29.5003V29.8137C0 34.0464 4.58142 36.6909 8.24692 34.5749L15.8951 30.1648L7.53961 22.7969L0 29.5003Z" fill="#ED2227" />
                        <path d="M15.1582 17.3549C13.5044 14.3415 10.2374 12.791 6.83565 13.4053C6.18723 13.5224 5.53788 13.7355 4.95586 14.0205L4.93649 14.0297C4.80551 14.0943 4.71051 14.2124 4.67546 14.3544L4.67085 14.3747C4.56293 14.8147 4.97246 15.0397 5.10713 15.1144L5.12189 15.1227C5.87454 15.5756 6.38277 15.8228 6.63181 15.8579C6.91775 15.8975 7.23135 15.8422 7.53389 15.7887C7.6953 15.7601 7.8475 15.7333 7.96279 15.7278C10.9596 15.5738 12.7388 17.5919 13.3531 19.3684C14.1399 21.6448 13.3965 23.9987 11.4595 25.3647C10.5547 26.003 8.76618 26.6588 7.76356 26.2318C7.39369 26.074 7.17785 25.7798 7.08838 25.3094C7.08285 25.279 7.08008 25.2467 7.08008 25.2162L7.07363 19.5012L7.06625 19.4579C7.00445 19.0862 6.8089 18.7947 6.46855 18.5659C6.27116 18.4331 6.05809 18.3234 5.85148 18.2173C5.70483 18.1417 5.56555 18.0697 5.44287 17.9959L5.3737 17.9535C5.04072 17.7506 4.61551 17.9959 4.62565 18.3861C4.62289 18.4294 4.61827 18.4995 4.61551 18.5429C4.60905 18.5844 4.60167 18.6342 4.59798 18.6914C4.52327 20.0141 4.55924 21.3958 4.59521 22.7314C4.62012 23.6823 4.64687 24.6665 4.63119 25.6018V25.6654L4.6441 25.7282C5.08407 27.7002 6.32097 28.4224 7.28116 28.6807C7.66948 28.7849 8.07624 28.8338 8.49131 28.8338C10.5279 28.8338 12.7748 27.6762 14.0421 26.3037C16.3379 23.817 16.776 20.3055 15.1582 17.3567V17.3549Z" fill="white" />
                        <defs>
                          <linearGradient id="paint0_linear_6572_1055" x1="0" y1="22.6111" x2="21.2275" y2="22.6111" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#F04C23" />
                            <stop offset="1" stopColor="#EA1D26" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </span>Pay via DramaFlicks App
                  </button>
                  {/* } */}

                {/* {currentCountry === "IN" && <button className={`${premium.paymentBtn} commonBtn`} onClick={(e) => handleSubmit(e, 'razorpay')}> */}
                  {/* {user && user?.premium_data?.premium == "1" ? 'Upgrade' : 'Continue'} With {selectedPlan} */}
                  {/* <span className={premium.commonBtnIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32" fill="none">
                      <path d="M11.9226 10.1219L10.4023 15.5629L19.0439 10.1219L13.4429 31.4858H19.0439L27.5655 0L11.9226 10.1219Z" fill="#3395FF" />
                      <path d="M11.8423 31.4846L16.7231 13.3213L2.32046 22.683L0 31.4846H11.8423Z" fill="#072654" />
                    </svg>
                  </span>Pay Securely with Razorpay
                </button>} */}
              </div>

            );
          })()}

          {/* ... (Rest of your component: FAQ, etc.) ... */}
          <div className={premium.UnlockPaid}>
            <p>
              Unlock all paid features with a Premium subscription. Billed
              weekly or monthly as per the selected plan. Subscriptions
              auto-renew unless canceled at least 24 hours before renewal, as
              per <Link href="https://www.paypal.com/us/cshelp/article/what-is-an-automatic-payment-and-how-do-i-update-or-cancel-one-help240">PayPal Policy</Link>. Payment is charged on purchase
              and within 24 hours before each renewal. Cancel anytime via PayPal
              settings. See our <Link href="/terms-of-use">Terms of Use</Link> and{" "}
              <Link href="/privacy-policy">Privacy Policy</Link> for details.
            </p>
          </div>

          <div className={premium.accordionSec}>
            <div className={premium.FaqSec}>
              <h3>Frequently Asked Questions:</h3>
            </div>

            <div className={premium.accordion}>
              <div className={premium.tab}>
                <input
                  type="checkbox"
                  name="accordion-1"
                  id="cb1"
                  defaultChecked
                />
                <label htmlFor="cb1" className={premium.tab__label}>
                  What do I get with a premium subscription?
                  <span className={premium.lableArrow_icon}><svg xmlns="http://www.w3.org/2000/svg" height="26px" viewBox="0 -960 960 960" width="26px" fill="#FFFFFF"><path d="M480-333 240-573l51-51 189 189 189-189 51 51-240 240Z" /></svg></span>
                </label>
                <div className={premium.tab__content}>
                  <p>
                    With premium, you unlock all current and future dramas,
                    unlimited viewing with no ad caps, and access across
                    multiple devices.
                  </p>
                </div>
              </div>
              <div className={premium.tab}>
                <input type="checkbox" name="accordion-1" id="cb2" />
                <label htmlFor="cb2" className={premium.tab__label}>
                  Can I watch for free?
                  <span className={premium.lableArrow_icon}><svg xmlns="http://www.w3.org/2000/svg" height="26px" viewBox="0 -960 960 960" width="26px" fill="#FFFFFF"><path d="M480-333 240-573l51-51 189 189 189-189 51 51-240 240Z" /></svg></span>
                </label>
                <div className={premium.tab__content}>
                  <p>
                    Yes! You can watch selected episodes daily by watching ads.
                    However, there&#39;s a daily limit. Upgrade for unlimited
                    streaming.
                  </p>
                </div>
              </div>

              <div className={premium.tab}>
                <input type="checkbox" name="accordion-1" id="cb3" />
                <label htmlFor="cb3" className={premium.tab__label}>
                  How many devices can I use with one subscription?
                  <span className={premium.lableArrow_icon}><svg xmlns="http://www.w3.org/2000/svg" height="26px" viewBox="0 -960 960 960" width="26px" fill="#FFFFFF"><path d="M480-333 240-573l51-51 189 189 189-189 51 51-240 240Z" /></svg></span>
                </label>
                <div className={premium.tab__content}>
                  <p>
                    You can watch on up to 3 devices with one premium account.
                  </p>
                </div>
              </div>

              <div className={premium.tab}>
                <input type="checkbox" name="accordion-1" id="cb4" />
                <label htmlFor="cb4" className={premium.tab__label}>
                  What happens when I cancel my subscription?
                  <span className={premium.lableArrow_icon}><svg xmlns="http://www.w3.org/2000/svg" height="26px" viewBox="0 -960 960 960" width="26px" fill="#FFFFFF"><path d="M480-333 240-573l51-51 189 189 189-189 51 51-240 240Z" /></svg></span>
                </label>
                <div className={premium.tab__content}>
                  <p>
                    You&#39;ll retain access until the end of your billing
                    cycle. After that, your account will switch to the free
                    plan.
                  </p>
                </div>
              </div>

              <div className={premium.tab}>
                <input type="checkbox" name="accordion-1" id="cb5" />
                <label htmlFor="cb5" className={premium.tab__label}>
                  {" "}
                  Are new dramas included in the subscription?
                  <span className={premium.lableArrow_icon}><svg xmlns="http://www.w3.org/2000/svg" height="26px" viewBox="0 -960 960 960" width="26px" fill="#FFFFFF"><path d="M480-333 240-573l51-51 189 189 189-189 51 51-240 240Z" /></svg></span>
                </label>
                <div className={premium.tab__content}>
                  <p>
                    Absolutely! All upcoming and newly launched dramas are
                    included in your premium plan at no extra cost.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
